import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Video, 
  Play, 
  Heart, 
  Eye, 
  Plus,
  Trash2,
  X,
  Camera,
  Loader2,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { API_URL } from '../utils/api';

export default function MyVideosPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVideo, setEditingVideo] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchMyVideos();
  }, [user]);

  const fetchMyVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-creator-posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVideos(data.posts || []);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load your videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingVideo) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/creator-posts/${deletingVideo.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setVideos(videos.filter(v => v.id !== deletingVideo.id));
        toast.success('Video deleted successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete video');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete video');
    } finally {
      setDeleting(false);
      setDeletingVideo(null);
    }
  };

  const handleThumbnailUpdated = (videoId, newThumbnailUrl) => {
    setVideos(videos.map(v => 
      v.id === videoId ? { ...v, thumbnail_url: newThumbnailUrl } : v
    ));
    setEditingVideo(null);
    toast.success('Cover photo updated!');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/creator')}
              className="flex items-center text-stone-600 hover:text-stone-800"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Feed
            </button>
            <h1 className="text-lg font-semibold text-stone-800">My Showroom</h1>
            <Button
              onClick={() => navigate('/creator/post')}
              size="sm"
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Post
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-stone-200">
            <Video className="mx-auto h-16 w-16 text-stone-300 mb-4" />
            <h3 className="text-xl font-medium text-stone-700 mb-2">No posts yet</h3>
            <p className="text-stone-500 mb-6">Share photos or videos of your work!</p>
            <Button
              onClick={() => navigate('/creator/post')}
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            >
              <Plus className="h-5 w-5 mr-2" />
              Post Your First Work
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onEdit={() => setEditingVideo(video)}
                onDelete={() => setDeletingVideo(video)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Cover Photo Modal */}
      {editingVideo && (
        <EditCoverModal
          video={editingVideo}
          token={token}
          onClose={() => setEditingVideo(null)}
          onUpdated={handleThumbnailUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingVideo} onOpenChange={() => setDeletingVideo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your post
              "{deletingVideo?.caption?.slice(0, 50)}..."
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-teal-600 hover:bg-gradient-to-r from-sky-500 to-teal-500"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Video Card Component with Edit/Delete options
function VideoCard({ video, onEdit, onDelete }) {
  const thumbnailSrc = video.thumbnail_url?.startsWith('/api')
    ? `${API_URL}${video.thumbnail_url}`
    : video.thumbnail_url;

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-stone-200">
      {/* Video Thumbnail */}
      <div className="relative aspect-video bg-stone-900">
        {thumbnailSrc ? (
          <img 
            src={thumbnailSrc}
            alt={video.caption}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
            <Video className="h-12 w-12 text-stone-600" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="h-6 w-6 text-white ml-0.5" fill="currentColor" />
          </div>
        </div>
        
        {/* Action Menu */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit} className="cursor-pointer">
                <Camera className="h-4 w-4 mr-2" />
                Change Cover Photo
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={onDelete} 
                className="cursor-pointer text-teal-600 focus:text-teal-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Video
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Video Info */}
      <div className="p-4">
        <p className="text-stone-700 text-sm line-clamp-2 mb-2">{video.caption}</p>
        
        {/* Tags */}
        {video.tags && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {video.tags.slice(0, 3).map(tag => (
              <span 
                key={tag}
                className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-stone-500">
          <span className="flex items-center gap-1">
            <Heart className="h-4 w-4" />
            {video.like_count || 0}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {video.view_count || 0}
          </span>
        </div>

        {/* Date */}
        <p className="text-xs text-stone-400 mt-2">
          Posted {new Date(video.created_at).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
        </p>
      </div>
    </div>
  );
}

// Edit Cover Photo Modal
function EditCoverModal({ video, token, onClose, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const thumbnailInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('file', file, 'thumbnail.jpg');

    try {
      // First upload the thumbnail
      const uploadResponse = await fetch(`${API_URL}/api/upload/thumbnail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.detail || 'Failed to upload thumbnail');
      }

      const uploadData = await uploadResponse.json();

      // Then update the creator post with the new thumbnail
      const updateResponse = await fetch(`${API_URL}/api/creator-posts/${video.id}/thumbnail`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ thumbnail_url: uploadData.thumbnail_url })
      });

      if (updateResponse.ok) {
        onUpdated(video.id, uploadData.thumbnail_url);
        toast.success('Cover photo updated!');
      } else {
        const error = await updateResponse.json();
        throw new Error(error.detail || 'Failed to update cover photo');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to update cover photo');
    } finally {
      setUploading(false);
    }
  };

  // Current thumbnail
  const currentThumbnail = video.thumbnail_url?.startsWith('/api')
    ? `${API_URL}${video.thumbnail_url}`
    : video.thumbnail_url;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-200">
          <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Camera className="h-5 w-5 text-teal-600" />
            Change Cover Photo
          </h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Current Cover Preview */}
          <div>
            <p className="text-sm text-stone-600 mb-2">Current cover photo:</p>
            <div className="aspect-video bg-stone-100 rounded-lg overflow-hidden">
              {currentThumbnail ? (
                <img 
                  src={currentThumbnail}
                  alt="Current cover"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-400">
                  <Video className="h-12 w-12" />
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Upload a new cover photo from your camera roll:
            </p>
            
            <Button
              onClick={() => thumbnailInputRef.current?.click()}
              disabled={uploading}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 py-6"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Camera className="h-5 w-5 mr-2" />
                  Choose Photo from Camera Roll
                </>
              )}
            </Button>
            
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Cancel Button */}
          <Button
            variant="outline"
            onClick={onClose}
            disabled={uploading}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
